/**
 * PAN verification rail (owner item 3: PAN as a PRE-CALL check).
 *
 * The backend endpoint has existed all along — `POST /api/v1/sessions/{id}/pan` verifies the
 * captured PAN against the issuing-source port and records the append-only evidence row + the
 * check-status projection. It is policy-gated (the session's PINNED regulatory policy must carry
 * PAN as OPTIONAL/REQUIRED — OFF answers 404 "not in flow", per the per-session regime gate) but deliberately NOT state-banded, so
 * the customer can capture pre-call exactly as the officer can in-call. What was missing was only
 * this client and a customer screen.
 *
 * EVIDENCE, NEVER A VERDICT: the response's `verdict` is an advisory the OFFICER confirms on the
 * call (the check-status projection records RUNNING, never a terminal). The step's copy must say
 * so — a customer must never read "VALID" as "you are approved".
 *
 * PII: the full PAN travels once, over this authenticated POST, and is never logged client-side.
 */

import { request } from './client';

export interface PanVerificationResultDTO {
  readonly sessionId: string;
  readonly attempt: number;
  /** Masked echo (never the full PAN back on the wire). */
  readonly panMasked?: string | null;
  /** VALID | NAME_MISMATCH | INVALID | NOT_FOUND | ERROR — advisory, officer-confirmed. */
  readonly verdict: string;
  readonly nameMatchScore?: number | null;
  readonly provider?: string | null;
  /** Honesty stamp (e.g. STUB_DETERMINISTIC_NOT_AI) — rendered, never hidden. */
  readonly certification?: string | null;
}

export interface PanCaptureInput {
  readonly pan: string;
  readonly nameAsPerDocument: string;
  readonly dobAsPerDocument?: string;
}

/** Verify the captured PAN. Throws ApiError (404 = PAN not in this session's pinned flow). */
export function capturePan(
  sessionId: string,
  input: PanCaptureInput,
): Promise<PanVerificationResultDTO> {
  return request<PanVerificationResultDTO>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/pan`,
    { method: 'POST', body: input },
  );
}

/** Prior attempts, oldest first (resume support: a prior VALID auto-advances the step). */
export function listPanAttempts(
  sessionId: string,
  signal?: AbortSignal,
): Promise<readonly PanVerificationResultDTO[]> {
  return request<readonly PanVerificationResultDTO[]>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/pan`,
    signal ? { signal } : {},
  );
}

/**
 * Testmap #11 (20-Aug live): the customer's audited OFFICER-VERIFY election on a PRE_CALL-pinned
 * PAN — `POST /pan/fallback:elect` (204), the be's droom-guarded mirror of the DigiLocker
 * `digilocker:fallback` route. Elected when the capture wire REFUSES the customer principal (403):
 * the be records PAN_SOURCE = FALLBACK_VERIFIED / CUSTOMER_ELECTED_OFFICER_VERIFY so the CF8 join
 * gate admits the customer and the officer runs the check live — the interim the testmap names,
 * made a first-class recorded act rather than a client-side shrug.
 */
export function electPanOfficerVerify(sessionId: string): Promise<void> {
  return request<void>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/pan/fallback:elect`,
    { method: 'POST' },
  );
}

/** Client-side PAN shape gate — the format is public (AAAAA9999A); a malformed entry never leaves the device. */
export const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
